import { useCallback, useContext, useState } from "react";
import { AxiosError } from "axios";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";
import { Task } from "../../../app/backendTypes";
import { useAppSelector } from "../../../app/hooks";
import { Role } from "../../../app/backendTypes";
import { Close } from "@mui/icons-material";
import Morselbar from "../Morselbar";

function CancelTask(props: { task: Task; callback: () => void }) {
  const task = props.task;
  const callback = props.callback;
  const statusKind = props.task.status.kind;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>("");

  const client = useContext(HttpClientContext);

  const cancelTask = useCallback(() => {
    setLoading(true);
    setError(undefined);
    const reason = cancelReason || null;

    client
      ?.post(`/api/tasks/${task.id}/cancel`, { reason })
      .then(() => {
        callback();
        setDialogOpen(false);
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [cancelReason, client, task, callback]);

  const auth = useAppSelector((state) => state.auth);

  const isAdmin = auth.roles.includes(Role.Administrator);

  if (task.parent.kind === "User") {
    if (task.parent.inner.id !== auth.userId && !isAdmin) return <></>;
  } else if (!isAdmin) return <></>;

  if (
    statusKind === "Running" ||
    statusKind === "Cancelled" ||
    statusKind === "Done"
  )
    return <></>;

  return (
    <>
      <Button
        color="error"
        disabled={loading}
        onClick={() => setDialogOpen(true)}
        variant="outlined"
      >
        Cancel
      </Button>

      <Dialog open={dialogOpen}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              This action will{" "}
              <Typography component="span" color="error" fontWeight="bold">
                cancel
              </Typography>{" "}
              the task or request
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <br />

          <TextField
            onChange={(e) => setCancelReason(e.target.value.trim())}
            placeholder="(Optional) Specify a reason for cancellation"
            label="Reason"
            fullWidth
            size="small"
          />

          <br />
          <br />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => cancelTask()}
              disabled={loading}
              color="error"
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to cancel task"
        error={error}
      />
    </>
  );
}

export default CancelTask;
